import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { LineStatus, MediaType, OrderDetailsViewType, SourceSystem } from 'src/app/models/enums';
import { OrderService } from '../../services/order/order.service';
import { EnvironmentService } from '../environment/environment.service';
import { ArcVersionMapping, OpenOrder, OrderLine, OrderShipment, OrderShipmentGroup } from './../../models';
import { CacheRefreshTimeService } from '../cache-refresh-time-service/cache-refresh-time.service';
import { OrderShipmentGroupsList } from '../../models/order-shipment-groups-list';

@Injectable({
  // we declare that this service should be created by the root application injector.
  providedIn: 'root'
})
export class OrderControllerService implements OnDestroy {
  public orderDataRefreshedEvent: Observable<null>;
  public timerRefreshedEvent: Subject<null> = new Subject<null>();
  public newOrderDetailsAvailableEvent: Subject<null> = new Subject<null>(); // things like resetting order lines need to refresh the UI

  // Store state of what the orders detail is. The components are dumb, so they don't retain any state
  public orderDetailsView: OrderDetailsViewType = OrderDetailsViewType.Grid;

  // A lot of things want to grab the order shipment data.
  // Grabbing the data from the API is very heavy, so keep the order data
  // Update the main data at the application level every X seconds
  private latestOrderData: OrderShipmentGroupsList = new OrderShipmentGroupsList();
  private latestOrderDataUpdateTime: Date;
  private orderDataRefreshed: Subject<null> = new Subject<null>(); // let other components know when data has changed

  // This data never changes, so only call this API one time and store it here
  private arcVersionMappingData: ArcVersionMapping[];
  private arcVersionMappings$: Observable<ArcVersionMapping[]>;

  private $unsubscribeSubject = new Subject();
  private autoRefreshTimer: number = null; // has javascript setTimeout() function to start/stop
  private initialLoadResetTimer: number = null; // same as above, but for the initial load of data

  constructor(
    private orderService: OrderService,
    private environmentService: EnvironmentService,
    private cacheRefreshService: CacheRefreshTimeService
  ) {
    this.orderDataRefreshedEvent = this.orderDataRefreshed.asObservable(); // components will subscribe to this for changes
  }

  public orderDetailsViewType(): OrderDetailsViewType {
    return this.orderDetailsView;
  }

  public setOrderDetailsViewType(type: OrderDetailsViewType): void {
    this.orderDetailsView = type;
  }

  public getAllOrderShipments(): OrderShipmentGroupsList {
    // this is what components will use to access the latest data
    return this.latestOrderData;
  }

  public getArcVersions(): ArcVersionMapping[] {
    return this.arcVersionMappingData;
  }

  /**
   * These are pass through functions that the view will call
   * This controller class is what the view calls for everything else API related. It seemed cleaner if the views would just
   * continue to call the same service instead of adding a new one to directly call a few random API calls
   */
  public getOpenOrders(search: number): Promise<OpenOrder[]> {
    return this.orderService.getOpenOrders(search).toPromise();
  }

  public getArcVersionMappings(): Promise<ArcVersionMapping[]> {
    // shareReplay caches the response and also guards against concurrent duplicate
    // HTTP calls while the first request is still in flight
    if (!this.arcVersionMappings$) {
      this.arcVersionMappings$ = this.orderService.getArcVersionMappings().pipe(
        tap((version: ArcVersionMapping[]) => {
          this.arcVersionMappingData = version;
        }),
        shareReplay(1),
        catchError((error) => {
          this.arcVersionMappings$ = null;
          return throwError(() => error);
        })
      );
    }

    return this.arcVersionMappings$.toPromise();
  }

  public searchAllOrdersByOrderNumber(orderNumber: number): Promise<OrderShipmentGroup[]> {
    return this.orderService.searchAllOrdersByOrderNumber(orderNumber).toPromise();
  }

  public searchAllOrdersByPortalId(portal: string): Promise<OrderShipmentGroup[]> {
    return this.orderService.getOrdersByPortalGuid(portal).toPromise();
  }

  ngOnDestroy() {
    this.stopAutoRefresh();
    clearInterval(this.initialLoadResetTimer);
    this.initialLoadResetTimer = null;
    this.$unsubscribeSubject.next();
    this.$unsubscribeSubject.complete();
  }

  public getOrderDetails(orderNumber: number, shipmentNumber: number, sourceSystem: SourceSystem): Observable<OrderShipment> {
    return this.orderService.getOrderDetails(orderNumber, shipmentNumber, sourceSystem).pipe(tap((shipment: OrderShipment) => {
      if (shipment) {
        if (this.isAdebsHardDriveNetworkShareDelivery(shipment)) {
          shipment.mediaType = MediaType.NetworkDelivery; // same adebs network share conversion that happens on getting order data
        }

        // we don't need to see any cancelled lines, so just remove those now
        shipment.lines = shipment.lines.filter((line: OrderLine) => line.status !== LineStatus.Cancelled);
      }
    }));
  }

  public getOrdersByCustomerNumber(customerNumber: number): Observable<OrderShipmentGroup[]> {
    return this.orderService.getAllOrderShipmentsByCustomerNumber(customerNumber);
  }

  public linkToPubAdmin(productNumber: string) {
    this.orderService.getProductId(productNumber).toPromise()
      .then(id => {
        const url = `${this.environmentService.getPubAdminBaseAddress()}title?productId=${id}`;
        window.open(url, "_blank").focus();
      }).
      catch(error => {
        console.log(error.error);
      });
  }

  public refreshAllShipmentsOneTime(): void {
    this.cacheRefreshService.updateCacheRefreshTimes();

    this.orderService.getAllOrderShipments().toPromise().then((orderShipGroup: OrderShipmentGroupsList) => {
      this.latestOrderData = orderShipGroup;
      this.latestOrderDataUpdateTime = new Date();

      // If we have data now, remove the more aggressive refresh timer to only have the one set by
      // the user
      if (orderShipGroup.cacheProcessingTime !== null) {
        console.log(`The order data is now available. Stopping the initial load refresh timer.`);
        clearInterval(this.initialLoadResetTimer);
        this.initialLoadResetTimer = null;
      }

      this.latestOrderData.orderShipmentGroups.forEach((orderShipment: OrderShipmentGroup) => {
        orderShipment.orderShipments.forEach((shipment: OrderShipment) => {
          if (this.isAdebsHardDriveNetworkShareDelivery(shipment)) {
            shipment.mediaType = MediaType.NetworkDelivery;
          }

          // convert date strings from server to just dates so they are easier to work with later
          shipment.orderEntryDate = new Date(shipment.orderEntryDate);
          shipment.needDate = new Date(shipment.needDate);
          shipment.shipDate = new Date(shipment.shipDate);
        });

        // sort order shipments by ship date so earliest appears first
        orderShipment.orderShipments.sort((a, b) => a.shipDate > b.shipDate ? 0 : -1);
      });

      this.orderDataRefreshed.next(null); // notify the components that data has changed
    });
  }

  public orderDataNeedsRefresh(): boolean {
    // If we don't have any data or the data is older than 5 minutes old
    const fiveMinutesAgo = Date.now() - (60 * 5 * 1000);
    return this.latestOrderData.orderShipmentGroups.length == 0 || this.latestOrderDataUpdateTime.getTime() < fiveMinutesAgo;
  }

  public triggerTimerRefreshedEvent(): void {
    console.log(`Timer to refresh data called at: ${new Date().toString()}`);
    this.timerRefreshedEvent.next(null); // notify the components that data has changed
  }

  public updateRefreshTimer(intervalMilliseconds: number, onOrderShipmentsPage: boolean): void {
    const self = this; // need to pass "this" as a local variable, otherwise setInterval cannot access scope

    // If we don't have any data yet, refresh every 30 seconds to get the data as soon as possible
    if (this.latestOrderData.cacheProcessingTime === null && this.initialLoadResetTimer === null) {
      let initialIntervalMilliseconds = 1000 * 5;
      console.log(`The order data is not yet available. Setting an additional refresh interval to ${initialIntervalMilliseconds / 1000} seconds.`);
      this.initialLoadResetTimer = window.setInterval(() => {
        this.refreshAllShipmentsOneTime();
        this.triggerTimerRefreshedEvent();
      }, initialIntervalMilliseconds, self);
    }

    console.log(`Setting order refresh timer to ${intervalMilliseconds / 1000} seconds. On order shipments page: ${onOrderShipmentsPage}.`)
    this.stopAutoRefresh();

    // using 'window' helps resolve typescript to know it returns a number
    // without window, typescript wants to convert it to a NodeJS.Timer type
    this.autoRefreshTimer = window.setInterval(() => {
      // If we're not on the main Order Shipments page, handle reloading here
      if (!onOrderShipmentsPage) {
        if (this.orderDataNeedsRefresh()) {
          this.refreshAllShipmentsOneTime();
        } else {
          console.log('No need to refresh data');
          // Trigger the event to make sure components have loaded it in
          this.orderDataRefreshed.next(null);
        }
      }

      this.triggerTimerRefreshedEvent();
    }, intervalMilliseconds, self);
  }

  public stopAutoRefresh(): void {
    clearInterval(this.autoRefreshTimer);
    this.autoRefreshTimer = null;
  }

  private isAdebsHardDriveNetworkShareDelivery(shipment: OrderShipment): boolean {
    if (shipment === null) { return false; };

    // The data is still marked as a hard drive in the database, so we are just "fixing" it on the javascript side
    if (shipment.mediaType === MediaType.HardDrive &&
      shipment.uploadShipmentToNetworkShare &&
      shipment.sourceSystem === SourceSystem.Adebs) {
      return true;
    }

    return false;
  }
}
